import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type EmployeeProfile,
  updateEmployeeEmail,
} from '../../../shared/api/employee.service';
import { RpcError } from '../../../shared/api/rpc-client';

type RegistrationEmailForm = {
  canSubmit: boolean;
  email: string;
  emailErrorMessage: string | null;
  isEmailInvalid: boolean;
  isSubmitting: boolean;
  setEmail: (nextEmail: string) => void;
  submit: () => Promise<boolean>;
  submitError: string | null;
};

type UseRegistrationEmailFormParams = {
  initialProfile?: EmployeeProfile | null;
};

const EMPLOYEE_PROFILE_QUERY_KEY = ['employee', 'profile'] as const;
const EMAIL_CONFLICT_ERROR = 'try err: conflict';

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isValidRegistrationEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(email));
}

function isEmailConflictError(error: unknown): boolean {
  return (
    error instanceof RpcError &&
    error.message.toLowerCase().includes(EMAIL_CONFLICT_ERROR)
  );
}

export function useRegistrationEmailForm({
  initialProfile = null,
}: UseRegistrationEmailFormParams = {}): RegistrationEmailForm {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmailValue] = useState(() => initialProfile?.email ?? '');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const profileSave = useMutation({
    mutationFn: updateEmployeeEmail,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: EMPLOYEE_PROFILE_QUERY_KEY,
        refetchType: 'all',
      });
    },
  });
  const hasEmailValue = email.trim().length > 0;
  const canSubmit = isValidRegistrationEmail(email);
  const shouldShowFormatError =
    (hasEmailValue || hasAttemptedSubmit) && !canSubmit && !profileSave.isError;
  const emailErrorMessage = isEmailConflictError(profileSave.error)
    ? t('registrationEmail.emailInUse')
    : shouldShowFormatError
    ? t('registrationEmail.invalidEmail')
    : null;
  const isEmailInvalid = emailErrorMessage !== null;

  const setEmail = (nextEmail: string) => {
    if (profileSave.isError) {
      profileSave.reset();
    }

    setEmailValue(nextEmail);
  };

  const submit = async () => {
    setHasAttemptedSubmit(true);

    if (!canSubmit || profileSave.isPending) {
      return false;
    }

    try {
      await profileSave.mutateAsync({
        email: normalizeEmail(email),
      });

      return true;
    } catch {
      return false;
    }
  };

  return {
    canSubmit,
    email,
    emailErrorMessage,
    isEmailInvalid,
    isSubmitting: profileSave.isPending,
    setEmail,
    submit,
    submitError:
      profileSave.error && !isEmailConflictError(profileSave.error)
        ? t('login.networkError')
        : null,
  };
}
